import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AuthContext } from "./useAuth";
import api from "./api";
import { getAccessToken, setAccessToken, logout } from "./authService";
import type { User, UserDetails } from "../../type";
import type {
  RegisterRequest,
  AuthResponseRegister,
  LoginApiResponse,
  VerifyEmailRequest,
  User as AuthApiUser,
} from "../services/auth.type";
import {
  googleAuth as googleAuthApi,
  appleAuth as appleAuthApi,
  logoutUser as logoutUserApi,
} from "../services/auth.api";
import {
  getPushTokenFields,
  registerDeviceToken,
} from "./pushToken";
import {
  resetAmplitudeUser,
  setAmplitudeUserId,
  trackEvent,
} from "../../analytics/amplitude";
import { setAmplitudeUserRole } from "../../analytics/amplitudeTracking";
import { identifyBrevoUser } from "../../analytics/brevo";
import { trackSignupCompleted } from "../../analytics/productEvents";
import { clearArticleSignupAttribution } from "../../analytics/articleSignupAttribution";
import {
  identifyPostHogUser,
  resetPostHogUser,
} from "../../analytics/posthog";
import { getStoredReferralCode } from "./referralCapture";
import { markAnnualDiscountPromoLogin } from "../../config/annualDiscountPromo";

// Auth endpoints return a partial user shape; this normalizes it into the full
// User & UserDetails type used across the app. The subsequent /users/:id/details
// fetch will fill in the real role/client_type/features values.
function buildUserWithDetails(apiUser: AuthApiUser): User & UserDetails {
  const convertedUser: User = {
    ...apiUser,
    birth_date: apiUser.birth_date || "",
    company: apiUser.company || "",
    failed_login_attempts: apiUser.failed_login_attempts || 0,
    linkedin: apiUser.linkedin || "",
    locked_until: apiUser.locked_until || "",
    note: apiUser.note || "",
    password_changed_at: apiUser.password_changed_at || "",
    pinfl: apiUser.pinfl || "",
    twitter: apiUser.twitter || "",
    two_factor_enabled: apiUser.two_factor_enabled || false,
    website: apiUser.website || "",
    role: {
      id: apiUser.role_id || "",
      name: "",
      client_type_id: apiUser.client_type_id || "",
      created_at: "",
      updated_at: "",
    },
  };

  return {
    ...convertedUser,
    user: convertedUser,
    role: convertedUser.role,
    client_type: {
      id: apiUser.client_type_id || "",
      name: "",
      created_at: "",
      updated_at: "",
    },
    has_pricing_plan: false,
    remaining_days: 0,
    features: {},
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // The session lives in localStorage, which the server can't read. Start
  // logged out on both server and hydrating client (so the HTML matches),
  // then restore before the first paint. Guards wait for isRestored.
  const [user, setUser] = useState<(User & UserDetails) | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [isRestored, setIsRestored] = useState(false);
  const identifiedUserIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch {
      localStorage.removeItem("user");
    }
    setTokenState(getAccessToken());
    setIsRestored(true);
  }, []);

  // Keep localStorage in sync with the user, once the stored one was read.
  useEffect(() => {
    if (!isRestored) return;
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user, isRestored]);

  useEffect(() => {
    const userId = user?.id || user?.user?.id;
    const distinctId = userId ? String(userId) : undefined;
    setAmplitudeUserId(distinctId);
    setAmplitudeUserRole(user?.role?.name);

    const email = user?.email || user?.user?.email;
    const firstName = user?.first_name || user?.user?.first_name;
    const lastName = user?.last_name || user?.user?.last_name;

    if (distinctId && identifiedUserIdRef.current !== distinctId) {
      if (identifiedUserIdRef.current) {
        resetPostHogUser();
      }

      identifyPostHogUser(distinctId, {
        ...(email ? { email } : {}),
        ...(firstName ? { first_name: firstName } : {}),
        ...(lastName ? { last_name: lastName } : {}),
        ...(user?.role?.name ? { role: user.role.name } : {}),
      });
      identifiedUserIdRef.current = distinctId;
    }

    if (email) {
      identifyBrevoUser(email, {
        ...(firstName ? { FIRSTNAME: firstName } : {}),
        ...(lastName ? { LASTNAME: lastName } : {}),
        ...(distinctId ? { id: distinctId } : {}),
      });
    }
  }, [user]);

  // Слушаем auth:logout (например, при 401 из api interceptor) для синхронизации состояния
  useEffect(() => {
    const handleAuthLogout = () => {
      trackEvent("User Signed Out", { reason: "session_expired" });
      resetAmplitudeUser();
      resetPostHogUser();
      identifiedUserIdRef.current = null;
      setUser(null);
      setTokenState(null);
    };
    window.addEventListener("auth:logout", handleAuthLogout);
    return () => window.removeEventListener("auth:logout", handleAuthLogout);
  }, []);

  // Refresh the stored user from /details whenever the session or the user
  // changes (app load, login). Prefer user.id, fall back to user.user.id.
  const sessionUserId = user?.id || user?.user?.id;
  useEffect(() => {
    if (token) {
      const userId = sessionUserId;
      if (userId) {
        api
          .get(`/users/${userId}/details`)
          .then((res) => {
            setUser(res.data.data);
            localStorage.setItem("user", JSON.stringify(res.data.data));
            // Session restored on app load: (re-)register the push token.
            void registerDeviceToken();
          })
          .catch(() => {
            logout();
            resetPostHogUser();
            identifiedUserIdRef.current = null;
            setUser(null);
            setTokenState(null);
          });
      }
    }
  }, [token, sessionUserId]);

  const login = async (
    login: string,
    password: string,
    rememberMe = false
  ) => {
    const res = await api.post<LoginApiResponse>("/auth/login", {
      login,
      password,
      remember_me: rememberMe,
      ...getPushTokenFields(),
    });

    const body = res.data;

    if (body.success === false) {
      throw new Error(body.message || body.error || "");
    }

    const payload = body.data;
    const user = payload?.user;
    const token = payload?.session?.token;

    if (!token || !user) {
      throw new Error(body.message || body.error || "");
    }

    setAccessToken(token);
    setTokenState(token);
    setUser(buildUserWithDetails(user));
    markAnnualDiscountPromoLogin();
    clearArticleSignupAttribution();
    trackEvent("User Signed In", { method: "email" });
    // Device-token registration is handled centrally by the token-change effect.
  };

  const register = async (data: RegisterRequest) => {
    // Регистрация только отправляет данные и возвращает success
    // Токен и user данные будут получены после верификации email
    const res = await api.post<AuthResponseRegister>("/auth/register", {
      ...data,
      ...getPushTokenFields(),
      ref_code: data.ref_code || getStoredReferralCode() || undefined,
    });
    const body = res.data;

    if (body.success === false) {
      throw new Error(
        body.message || body.error || body.data?.message || "",
      );
    }

    return body;
  };

  const verifyEmail = async (data: VerifyEmailRequest) => {
    try {
      const res = await api.post("/auth/verify-email", data);

      // API может вернуть { user, session } или { data: { user, session } }
      const responseData = res.data.user ? res.data : res.data.data;

      const { user: apiUser, session } = responseData;
      const accessToken = session?.token;

      if (!accessToken) {
        console.error("No access token in response");
        throw new Error("Invalid response: missing access token");
      }

      if (!apiUser) {
        console.error("No user data in response");
        throw new Error("Invalid response: missing user data");
      }

      setAccessToken(accessToken);
      setTokenState(accessToken);

      setUser(buildUserWithDetails(apiUser));
      markAnnualDiscountPromoLogin();
      trackSignupCompleted("email");
      // user будет синхронизирован с localStorage через useEffect
    } catch (error) {
      console.error("Verify email error in AuthProvider:", error);
      throw error; // Пробрасываем ошибку дальше
    }
  };

  const googleAuth = async (idToken: string) => {
    try {
      const response = await googleAuthApi({
        id_token: idToken,
        ref_code: getStoredReferralCode() || undefined,
      });
      const apiUser = response?.data?.user;
      const session = response?.data?.session;

      const accessToken = session?.token;

      if (!accessToken) {
        console.error("No access token in response");
        throw new Error("Invalid response: missing access token");
      }

      if (!apiUser) {
        console.error("No user data in response");
        throw new Error("Invalid response: missing user data");
      }

      setAccessToken(accessToken);
      setTokenState(accessToken);

      setUser(buildUserWithDetails(apiUser));
      markAnnualDiscountPromoLogin();
      if (response?.data?.is_new_user) {
        trackSignupCompleted("google");
      } else {
        clearArticleSignupAttribution();
        trackEvent("User Signed In", { method: "google" });
      }
      // user будет синхронизирован с localStorage через useEffect
    } catch (error) {
      console.error("Google auth error in AuthProvider:", error);
      throw error;
    }
  };

  const appleAuth = async (idToken: string, rememberMe = true) => {
    try {
      const response = await appleAuthApi({
        id_token: idToken,
        remember_me: rememberMe,
        ref_code: getStoredReferralCode() || undefined,
      });
      const apiUser = response?.data?.user;
      const session = response?.data?.session;

      const accessToken = session?.token;

      if (!accessToken) {
        console.error("No access token in response");
        throw new Error("Invalid response: missing access token");
      }

      if (!apiUser) {
        console.error("No user data in response");
        throw new Error("Invalid response: missing user data");
      }

      setAccessToken(accessToken);
      setTokenState(accessToken);

      setUser(buildUserWithDetails(apiUser));
      markAnnualDiscountPromoLogin();
      if (response?.data?.is_new_user) {
        trackSignupCompleted("apple");
      } else {
        clearArticleSignupAttribution();
        trackEvent("User Signed In", { method: "apple" });
      }
    } catch (error) {
      console.error("Apple auth error in AuthProvider:", error);
      throw error;
    }
  };

  const handleLogout = () => {
    trackEvent("User Signed Out");
    resetAmplitudeUser();
    resetPostHogUser();
    identifiedUserIdRef.current = null;

    // Best-effort logout call carrying the FCM token so the backend can drop the
    // device registration. The access token must stay in storage until the
    // request is dispatched, so we clear it in `finally`.
    const { fcm_token } = getPushTokenFields();
    void logoutUserApi(fcm_token ? { fcm_token } : {})
      .catch(() => {
        // Ignore network/4xx errors: logout must always succeed locally.
      })
      .finally(() => {
        logout();
      });

    setUser(null);
    setTokenState(null);
    // user будет удалён из localStorage через useEffect

    // Диспатчим событие для очистки Google SDK
    window.dispatchEvent(new Event("auth:logout"));
  };

  // Refresh user data from API
  const refreshUser = async () => {
    if (!token) return;

    const userId = user?.id || user?.user?.id;
    if (!userId) return;

    try {
      const res = await api.get(`/users/${userId}/details`);
      setUser(res.data.data);
      localStorage.setItem("user", JSON.stringify(res.data.data));
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      // Don't logout on refresh failure, just log the error
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isRestored,
        login,
        register,
        verifyEmail,
        googleAuth,
        appleAuth,
        logout: handleLogout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
